import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine,
} from 'recharts';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency } from '../../utils/formatting';
import { useFinancialData, ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';
import { usePipeline } from '../../context/PipelineContext';
import { loadCashFlowActuals } from '../../utils/cashFlowLoader';
import { getFirmSetting, setFirmSetting } from '../../utils/firmSettings';
import { subscribeFirmSettings } from '../../utils/firmSettingsStore';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const LS_CF_KEY = 'bcc_cf_overrides';
const LS_CF_DEFAULTS_KEY = 'bcc_cf_defaults';

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

// 2025 actual distributions by month (from Statement of Cash Flows)
import overridesConfig from '../../config/overrides.json';
const DISTRIBUTIONS_2025 = overridesConfig.distributions2025 || new Array(12).fill(0);

function buildDefaultOverrides() {
  const overrides = {};
  for (let i = ACTUALS_CUTOFF_2026; i < 12; i++) {
    overrides[i] = { investingCF: 0, distributions: DISTRIBUTIONS_2025[i] };
  }
  return overrides;
}

function loadSavedDefaults() {
  const fromSettings = getFirmSetting('cfDefaults', null);
  if (fromSettings) return fromSettings;
  try {
    const stored = localStorage.getItem(LS_CF_DEFAULTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return buildDefaultOverrides();
}

function loadOverrides() {
  const fromSettings = getFirmSetting('cfOverrides', null);
  if (fromSettings) return fromSettings;
  try {
    const stored = localStorage.getItem(LS_CF_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return loadSavedDefaults();
}

function InlineCell({ value, editable, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  if (editing) {
    return (
      <input ref={inputRef} type="number" value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { const p = parseFloat(draft); if (!isNaN(p)) onChange(p); setEditing(false); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { const p = parseFloat(draft); if (!isNaN(p)) onChange(p); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
        className="w-full px-1 py-0.5 text-[12px] text-right border border-bullogic-blue rounded-[2px] outline-none bg-white"
      />
    );
  }

  return (
    <span
      onClick={() => { if (!editable) return; setDraft(value?.toString() || '0'); setEditing(true); }}
      className={`block text-right ${editable ? 'cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1' : ''} ${value < 0 ? 'text-rose' : ''}`}
    >
      {formatCurrency(value)}
    </span>
  );
}

export default function CashFlow({ monthlyImpact }) {
  const { global2026Monthly, forecastScenario } = useFinancialData();
  const { baselineForecastRevenue } = usePipeline();
  // Load QB-synced actuals if available, otherwise use JSON file
  const cashFlowData = useMemo(() => loadCashFlowActuals(), []);
  const [showScenarioImpact, setShowScenarioImpact] = useState(false);
  // Persist forecast toggle state in localStorage
  const [showForecast, setShowForecast] = useState(() => {
    try { const v = localStorage.getItem('bcc_cf_show_forecast'); return v !== null ? v === 'true' : false; } catch { return false; }
  });
  const [threshold, setThreshold] = useState(cashFlowData.minimumThreshold);
  const [overrides, setOverrides] = useState(loadOverrides);
  const [saveConfirm, setSaveConfirm] = useState(false);

  // Re-hydrate when FirmSettingsProvider's async load completes.
  useEffect(() => {
    const off = subscribeFirmSettings((fs) => {
      if (fs.cfOverrides) setOverrides(fs.cfOverrides);
    });
    return off;
  }, []);

  const toggleForecast = useCallback(() => {
    setShowForecast(prev => {
      const next = !prev;
      try { localStorage.setItem('bcc_cf_show_forecast', String(next)); } catch {}
      return next;
    });
  }, []);

  const updateOverride = useCallback((monthIdx, key, value) => {
    setOverrides((prev) => {
      const updated = { ...prev, [monthIdx]: { ...prev[monthIdx], [key]: value } };
      setFirmSetting('cfOverrides', updated);
      try { localStorage.setItem(LS_CF_KEY, JSON.stringify(updated)); } catch (e) { /* ignore */ }
      return updated;
    });
  }, []);

  const saveAsDefault = useCallback(() => {
    setFirmSetting('cfDefaults', overrides);
    try { localStorage.setItem(LS_CF_DEFAULTS_KEY, JSON.stringify(overrides)); } catch (e) { /* ignore */ }
    setSaveConfirm(true);
    setTimeout(() => setSaveConfirm(false), 2000);
  }, [overrides]);

  const resetToDefaults = useCallback(() => {
    const defaults = loadSavedDefaults();
    setOverrides(defaults);
    setFirmSetting('cfOverrides', null);
    try { localStorage.removeItem(LS_CF_KEY); } catch (e) { /* ignore */ }
  }, []);

  const rows = useMemo(() => {
    const result = [];
    let runningBalance = cashFlowData.startingBalance;

    // Use the number of actual months from the loaded data (may come from QB sync)
    const actualMonthCount = cashFlowData.months.length;

    for (let i = 0; i < 12; i++) {
      const isActual = i < actualMonthCount;
      let operatingCF, investingCF, distributions, otherFinancing;

      if (isActual) {
        const ad = cashFlowData.months[i];
        operatingCF = ad.operatingCF;
        investingCF = ad.investingCF;
        distributions = ad.distributions;
        otherFinancing = ad.otherFinancing || 0;
      } else {
        const mk = MONTH_KEYS[i];
        const monthData = global2026Monthly?.months?.[mk];
        operatingCF = sumCat(monthData, 'income') - sumCat(monthData, 'cogs') - sumCat(monthData, 'expenses');

        const mo = overrides[i] || {};
        investingCF = mo.investingCF ?? 0;
        distributions = mo.distributions ?? DISTRIBUTIONS_2025[i];
        otherFinancing = 0;
      }

      const scenarioDelta = showScenarioImpact ? (monthlyImpact?.[i]?.net || 0) : 0;
      // Forecast delta: pipeline + velocity revenue from PipelineContext
      // (already scenario-adjusted at the source). Only for projected months.
      const forecastDelta = !isActual && showForecast
        ? (baselineForecastRevenue?.global?.[i] || 0)
        : 0;
      const beginBalance = i === 0 ? cashFlowData.startingBalance : runningBalance;
      const netChange = operatingCF + investingCF + distributions + otherFinancing + scenarioDelta + forecastDelta;
      const endingBalance = beginBalance + netChange;

      result.push({
        idx: i, label: MONTH_LABELS[i], actual: isActual,
        beginBalance, operatingCF, investingCF, distributions, otherFinancing,
        scenarioDelta, forecastDelta, netChange, endingBalance,
        signal: endingBalance >= threshold ? 'OK' : 'WATCH',
      });

      runningBalance = endingBalance;
    }
    return result;
  }, [global2026Monthly, overrides, showScenarioImpact, showForecast, monthlyImpact, baselineForecastRevenue, threshold, cashFlowData]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <SectionLabel>Cash Flow Projection (2026)</SectionLabel>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-navy">Threshold</span>
            <div className="flex items-center gap-1">
              <span className="text-[12px] text-[#888]">$</span>
              <input type="number" value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                className="w-[80px] px-2 py-1 text-[13px] text-right border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
              />
            </div>
          </div>
          <button onClick={() => setShowScenarioImpact(!showScenarioImpact)}
            className={`px-3 py-1.5 text-[12px] font-bold rounded-[4px] transition-colors ${
              showScenarioImpact ? 'bg-bullogic-blue text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}>
            Scenarios: {showScenarioImpact ? 'ON' : 'OFF'}
          </button>
          <button onClick={toggleForecast}
            className={`px-3 py-1.5 text-[12px] font-bold rounded-[4px] transition-colors ${
              showForecast ? 'bg-deep-teal text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title="Add pipeline + velocity forecast revenue to projected months (uses active scenario)">
            Forecast: {showForecast ? 'ON' : 'OFF'}
          </button>
          <div className="flex items-center gap-1.5">
            <button onClick={saveAsDefault}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
                saveConfirm ? 'bg-deep-teal text-white' : 'text-deep-teal bg-deep-teal/10 hover:bg-deep-teal/20'
              }`}>
              {saveConfirm ? 'Saved!' : 'Save as Default'}
            </button>
            <button onClick={resetToDefaults}
              className="px-3 py-1.5 text-[11px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200 transition-colors">
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] font-light text-[#888] mb-3">
        <span>
          {cashFlowData.source === 'quickbooks'
            ? `Actuals synced from QuickBooks (Jan-${MONTH_LABELS[cashFlowData.months.length - 1]?.substring(0, 3)}).`
            : 'Jan-Mar actuals from local data.'
          }
          {' '}Apr-Dec: Operating CF = P&L Net Income.
          {showForecast && ' Forecast = Pipeline + Velocity revenue (active scenario).'}
          {' '}Click Investing or Distributions to edit per month.
        </span>
        {cashFlowData.source === 'quickbooks' && (
          <span className="px-1.5 py-0.5 text-[9px] font-bold bg-deep-teal/15 text-deep-teal rounded-[3px] uppercase tracking-wider whitespace-nowrap">
            QB Synced
          </span>
        )}
        {showForecast && (
          <span className="px-1.5 py-0.5 text-[9px] font-bold bg-deep-teal/15 text-deep-teal rounded-[3px] uppercase tracking-wider whitespace-nowrap">
            {forecastScenario} Forecast
          </span>
        )}
      </div>

      {/* Cash Balance Chart */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-4">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart
            data={rows.map((r) => ({
              month: r.label.substring(0, 3),
              balance: r.endingBalance,
              isActual: r.actual,
            }))}
            margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#006D78" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#006D78" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#888' }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
            <Tooltip
              formatter={(value) => [formatCurrency(value), 'Ending Balance']}
              contentStyle={{ fontSize: 13, borderRadius: 4, border: '1px solid #e5e7eb' }}
            />
            <ReferenceLine
              y={threshold}
              stroke="#FF6E83"
              strokeDasharray="6 3"
              strokeWidth={1.5}
              label={{ value: `Min: ${formatCurrency(threshold)}`, position: 'right', fontSize: 10, fill: '#FF6E83' }}
            />
            <Area
              type="monotone"
              dataKey="balance"
              stroke="#006D78"
              strokeWidth={2.5}
              fill="url(#balanceGrad)"
              dot={(props) => {
                const { cx, cy, index } = props;
                const isActual = rows[index]?.actual;
                return (
                  <circle
                    key={index}
                    cx={cx} cy={cy} r={isActual ? 5 : 3}
                    fill={isActual ? '#006D78' : '#3EAAD0'}
                    stroke="white" strokeWidth={2}
                  />
                );
              }}
              activeDot={{ r: 6, stroke: '#006D78', strokeWidth: 2, fill: 'white' }}
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-6 mt-2 text-[11px] text-[#888]">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-deep-teal" /> Actual
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-sky-blue" /> Projected
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 border-t-2 border-dashed border-rose" /> Min. Threshold
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)]">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-deep-teal text-white">
              <th className="px-3 py-2.5 text-left font-bold">Month</th>
              <th className="px-3 py-2.5 text-center font-bold w-[45px]">Type</th>
              <th className="px-3 py-2.5 text-right font-bold">Begin Balance</th>
              <th className="px-3 py-2.5 text-right font-bold">Operating CF</th>
              <th className="px-3 py-2.5 text-right font-bold">Investing CF</th>
              <th className="px-3 py-2.5 text-right font-bold">Distributions</th>
              <th className="px-3 py-2.5 text-right font-bold">Other Fin.</th>
              {showScenarioImpact && (
                <th className="px-3 py-2.5 text-right font-bold">Scenario</th>
              )}
              {showForecast && (
                <th className="px-3 py-2.5 text-right font-bold">Forecast</th>
              )}
              <th className="px-3 py-2.5 text-right font-bold">Net Change</th>
              <th className="px-3 py-2.5 text-right font-bold">Ending Balance</th>
              <th className="px-3 py-2.5 text-center font-bold w-[55px]">Signal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isWatch = row.signal === 'WATCH';
              const isEditable = !row.actual;
              return (
                <tr key={row.idx}
                  className={`border-b border-gray-100 ${
                    isWatch ? 'bg-rose/5' : row.idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}>
                  <td className="px-3 py-2 font-bold text-navy">{row.label}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-[3px] ${
                      row.actual ? 'bg-deep-teal/15 text-deep-teal' : 'bg-gray-100 text-[#888]'
                    }`}>{row.actual ? 'ACT' : 'PROJ'}</span>
                  </td>
                  <td className="px-3 py-2 text-right">{formatCurrency(row.beginBalance)}</td>
                  {/* Operating CF - locked (from P&L or statement) */}
                  <td className={`px-3 py-2 text-right ${row.operatingCF < 0 ? 'text-rose' : ''}`}>
                    {formatCurrency(row.operatingCF)}
                  </td>
                  {/* Investing CF - editable for projections */}
                  <td className="px-3 py-2">
                    <InlineCell value={row.investingCF} editable={isEditable}
                      onChange={(val) => updateOverride(row.idx, 'investingCF', val)} />
                  </td>
                  {/* Distributions - editable for projections */}
                  <td className="px-3 py-2">
                    <InlineCell value={row.distributions} editable={isEditable}
                      onChange={(val) => updateOverride(row.idx, 'distributions', val)} />
                  </td>
                  {/* Other Financing - locked (actuals only, 0 for projections) */}
                  <td className={`px-3 py-2 text-right ${row.otherFinancing === 0 ? 'text-[#ccc]' : ''}`}>
                    {row.otherFinancing !== 0 ? formatCurrency(row.otherFinancing) : '--'}
                  </td>
                  {showScenarioImpact && (
                    <td className={`px-3 py-2 text-right font-bold ${
                      row.scenarioDelta < 0 ? 'text-rose' : row.scenarioDelta > 0 ? 'text-deep-teal' : 'text-[#888]'
                    }`}>{row.scenarioDelta !== 0 ? formatCurrency(row.scenarioDelta) : '--'}</td>
                  )}
                  {showForecast && (
                    <td className={`px-3 py-2 text-right font-bold ${
                      row.forecastDelta > 0 ? 'text-deep-teal' : 'text-[#ccc]'
                    }`}>{row.forecastDelta > 0 ? formatCurrency(row.forecastDelta) : '--'}</td>
                  )}
                  <td className={`px-3 py-2 text-right font-bold ${row.netChange < 0 ? 'text-rose' : 'text-deep-teal'}`}>
                    {formatCurrency(row.netChange)}
                  </td>
                  <td className={`px-3 py-2 text-right font-bold ${isWatch ? 'text-rose' : ''}`}>
                    {formatCurrency(row.endingBalance)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-[4px] text-[10px] font-bold text-white ${
                      isWatch ? 'bg-rose' : 'bg-deep-teal'
                    }`}>{row.signal}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-deep-teal block mb-1">Year-End Balance</span>
          <span className={`text-[24px] font-bold ${rows[11]?.endingBalance >= threshold ? 'text-deep-teal' : 'text-rose'}`}>
            {formatCurrency(rows[11]?.endingBalance)}</span>
        </div>
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-navy block mb-1">Lowest Balance</span>
          {(() => {
            const minRow = rows.reduce((min, r) => r.endingBalance < min.endingBalance ? r : min, rows[0]);
            return (<>
              <span className={`text-[24px] font-bold ${minRow.endingBalance >= threshold ? 'text-deep-teal' : 'text-rose'}`}>
                {formatCurrency(minRow.endingBalance)}</span>
              <span className="text-[11px] font-light text-[#888] block mt-1">{minRow.label}</span>
            </>);
          })()}
        </div>
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-rose block mb-1">Months Below Threshold</span>
          <span className={`text-[24px] font-bold ${rows.filter(r => r.signal === 'WATCH').length > 0 ? 'text-rose' : 'text-deep-teal'}`}>
            {rows.filter(r => r.signal === 'WATCH').length}</span>
        </div>
        {(() => {
          const yearChange = (rows[11]?.endingBalance || 0) - cashFlowData.startingBalance;
          return (
            <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-navy block mb-1">Net Cash Change</span>
              <span className={`text-[24px] font-bold ${yearChange >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
                {yearChange >= 0 ? '+' : ''}{formatCurrency(yearChange)}</span>
              <span className="text-[11px] font-light text-[#888] block mt-1">
                {formatCurrency(cashFlowData.startingBalance)} to {formatCurrency(rows[11]?.endingBalance)}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
